import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { usePeer } from '@/contexts/WebRTCContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { availableLanguages, getDefaultLanguageForCountry, Language } from '@/services/languageMapping';
import { ChatPanelProps, Message } from './types';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';

// Re-export Message type for external use
export type { Message };

/**
 * Main chat panel component - orchestrates chat functionality
 * Declarative pattern: delegates rendering to specialized components
 */
export function ChatPanel({
  messages,
  isChatOpen,
  onToggleTranslation = () => {},
  autoTranslate = false,
  onToggleAutoTranslate = () => {},
  handleAddMessage,
  handleToggleChat,
  chatChannel,
  peerConnectionState,
  triggerResetConnection,
  setTriggerResetConnection,
  onUpdateTranslation = () => {}
}: ChatPanelProps) {
  const { translateText, isTranslating } = useTranslation();
  const [chatUnavailable, setChatUnavailable] = useState(false);
  const [resetChatPanel, setResetChatPanel] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const { userCountry } = usePeer();
  const { user } = useAuth();

  // Get the user's country code - prefer fingerprint-verified country from auth
  const userCountryCode = user?.maybeLocationInfoFE?.countryCode || user?.countryCode || userCountry?.code;

  // Language selection with country-based default
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(() => {
    return availableLanguages.find(lang => lang.code === 'EN') || availableLanguages[0];
  });

  // Set default language based on user's detected country (fingerprint > IP-based)
  useEffect(() => {
    if (userCountryCode) {
      const countryLanguage = getDefaultLanguageForCountry(userCountryCode);
      setSelectedLanguage(countryLanguage);
    }
  }, [userCountryCode]);

  // Handle message translation
  const handleTranslateMessage = useCallback(async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) return;

    if (message.translation) {
      onToggleTranslation(messageId);
      return;
    }

    const result = await translateText(message.text, selectedLanguage.code.toLowerCase());
    if (result) {
      onUpdateTranslation(messageId, result.translatedText);
    }
  }, [messages, selectedLanguage.code, translateText, onUpdateTranslation, onToggleTranslation]);

  // Auto-translate new peer messages
  useEffect(() => {
    if (!autoTranslate) return;

    const untranslatedPeerMessages = messages.filter(
      msg => msg.sender === 'peer' && !msg.translation && msg.text
    );

    untranslatedPeerMessages.forEach(async (message) => {
      const result = await translateText(message.text, selectedLanguage.code.toLowerCase());
      if (result) {
        onUpdateTranslation(message.id, result.translatedText);
      }
    });
  }, [messages, autoTranslate, selectedLanguage.code, translateText, onUpdateTranslation]);

  // Reset handling
  useEffect(() => {
    if (triggerResetConnection) {
      setResetChatPanel(true);
    }
  }, [triggerResetConnection]);

  useEffect(() => {
    if (!resetChatPanel) return;
    console.log('resetting chat panel');
    setResetChatPanel(false);
  }, [resetChatPanel]);

  // Handle send message
  const handleSendMessage = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const chatMsg = messageInput.trim();
    if (!chatMsg) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'self',
      text: chatMsg,
      time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };

    handleAddMessage(newMessage);

    if (chatChannel?.readyState === 'open') {
      chatChannel.send(chatMsg);
    } else {
      console.warn('chat channel is not open');
    }

    setMessageInput('');
  }, [messageInput, handleAddMessage, chatChannel]);

  // Determine if chat is disabled
  const isChatDisabled = peerConnectionState !== 'connected' ||
                         !chatChannel ||
                         chatChannel.readyState !== 'open' ||
                         chatUnavailable;

  const mobileHeightClass = isChatOpen ? 'h-80' : 'h-0';

  return (
    <div className={`w-full md:w-96 bg-surface flex flex-col border-t md:border-t-0 md:border-l border-gray-700 ${mobileHeightClass} md:h-auto transition-all duration-300`}>
      {/* Header with controls */}
      <ChatHeader
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        availableLanguages={availableLanguages}
        autoTranslate={autoTranslate}
        onToggleAutoTranslate={onToggleAutoTranslate}
        onClose={() => handleToggleChat(false)}
      />

      {/* Message list */}
      <MessageList
        messages={messages}
        autoTranslate={autoTranslate}
        selectedLanguageCode={selectedLanguage.code}
        isTranslating={isTranslating}
        onToggleTranslation={onToggleTranslation}
        onTranslateMessage={handleTranslateMessage}
      />

      {/* Message input */}
      <MessageInput
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        onSendMessage={handleSendMessage}
        isDisabled={isChatDisabled}
      />
    </div>
  );
}
